import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';
import { createVehicle } from '../lib/vehicles';
import { useAuth } from '../lib/auth';
import PhotoUploader from '../components/PhotoUploader';

const initial = {
  make: '', model: '', year: '', price: '', mileage: '', location: '',
  hp: '', transmission: '', drivetrain: '', condition: '', vin: '',
  desc: '',
  seller_email: '', seller_name: '',
};

export default function Sell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [f, setF] = React.useState(() => ({
    ...initial,
    seller_email: user?.email || localStorage.getItem('am_billing_email') || '',
    seller_name: user?.name || '',
  }));
  const [photos, setPhotos] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  React.useEffect(() => {
    if (user) {
      setF((prev) => ({
        ...prev,
        seller_email: prev.seller_email || user.email,
        seller_name: prev.seller_name || user.name || '',
      }));
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.make || !f.model || !f.price || !f.year) return toast.error('Make, model, year, and price are required');
    if (!/\S+@\S+\.\S+/.test(f.seller_email)) return toast.error('Enter a valid seller email');
    try {
      setSubmitting(true);
      const payload = {
        title: `${f.year} ${f.make} ${f.model}`,
        make: f.make.trim(),
        model: f.model.trim(),
        year: parseInt(f.year, 10),
        price: parseInt(String(f.price).replace(/[^\d]/g, ''), 10),
        mileage: parseInt(String(f.mileage || 0).replace(/[^\d]/g, ''), 10),
        location: f.location.trim() || 'Canada',
        description: f.desc.trim(),
        hp: f.hp ? parseInt(f.hp, 10) : null,
        transmission: f.transmission || null,
        drivetrain: f.drivetrain || null,
        condition: f.condition || null,
        vin: f.vin || null,
        seller_email: f.seller_email.trim(),
        seller_name: f.seller_name.trim() || null,
        photos,
        tag: null,
      };
      const created = await createVehicle(payload);
      localStorage.setItem('am_billing_email', f.seller_email);
      toast.success('Listing published');
      navigate(`/vehicles/${created.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Publish failed');
    } finally {
      setSubmitting(false);
    }
  };

  const input = 'w-full bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]';
  const label = 'text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1 block';

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-14" data-testid="page-sell">
      <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">Sell your vehicle</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">List it. Sell it. Done.</h1>
      <p className="mt-3 text-zinc-400 text-sm">Commission-based. No listing fee. You keep more of the sale.</p>

      <form onSubmit={submit} className="mt-10 am-card p-6 grid md:grid-cols-2 gap-4" data-testid="sell-form">
        <div><label className={label}>Make *</label><input data-testid="sell-make" className={input} value={f.make} onChange={set('make')} placeholder="Subaru" /></div>
        <div><label className={label}>Model *</label><input data-testid="sell-model" className={input} value={f.model} onChange={set('model')} placeholder="WRX STI" /></div>
        <div><label className={label}>Year *</label><input data-testid="sell-year" type="number" className={input} value={f.year} onChange={set('year')} placeholder="2019" /></div>
        <div><label className={label}>Price (CAD) *</label><input data-testid="sell-price" type="number" className={input} value={f.price} onChange={set('price')} placeholder="34500" /></div>
        <div><label className={label}>Mileage (km)</label><input data-testid="sell-mileage" type="number" className={input} value={f.mileage} onChange={set('mileage')} placeholder="42000" /></div>
        <div><label className={label}>Location</label><input data-testid="sell-location" className={input} value={f.location} onChange={set('location')} placeholder="Toronto, ON" /></div>
        <div><label className={label}>Power (HP)</label><input data-testid="sell-hp" type="number" className={input} value={f.hp} onChange={set('hp')} placeholder="310" /></div>
        <div>
          <label className={label}>Transmission</label>
          <select data-testid="sell-transmission" className={input} value={f.transmission} onChange={set('transmission')}>
            <option value="">Select…</option><option>Manual</option><option>Automatic</option><option>DCT</option><option>CVT</option>
          </select>
        </div>
        <div>
          <label className={label}>Drivetrain</label>
          <select data-testid="sell-drivetrain" className={input} value={f.drivetrain} onChange={set('drivetrain')}>
            <option value="">Select…</option><option>FWD</option><option>RWD</option><option>AWD</option><option>4WD</option>
          </select>
        </div>
        <div>
          <label className={label}>Condition</label>
          <select data-testid="sell-condition" className={input} value={f.condition} onChange={set('condition')}>
            <option value="">Select…</option><option>New</option><option>Excellent</option><option>Good</option><option>Fair</option><option>Project</option>
          </select>
        </div>
        <div className="md:col-span-2"><label className={label}>VIN (optional)</label><input data-testid="sell-vin" className={input} value={f.vin} onChange={set('vin')} placeholder="17-character VIN" /></div>

        <div className="md:col-span-2">
          <label className={label}>Photos</label>
          <PhotoUploader value={photos} onChange={setPhotos} />
        </div>
        <div className="md:col-span-2">
          <label className={label}>Description</label>
          <textarea data-testid="sell-desc" rows={5} className={input} value={f.desc} onChange={set('desc')} placeholder="Tell buyers what makes it special — mods, service history, previous owners…" />
        </div>

        <div className="md:col-span-2 hairline-t pt-4"><div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Seller contact</div></div>
        <div><label className={label}>Your name</label><input data-testid="sell-seller-name" className={input} value={f.seller_name} onChange={set('seller_name')} placeholder="Alex M." /></div>
        <div><label className={label}>Your email *</label><input data-testid="sell-seller-email" type="email" className={input} value={f.seller_email} onChange={set('seller_email')} placeholder="you@example.com" /></div>

        <div className="md:col-span-2 flex items-center justify-between gap-3 pt-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-400" /> PIPEDA-compliant · Contact stays private
          </div>
          <button data-testid="sell-submit" disabled={submitting} type="submit" className="bg-[#ff2a2a] hover:bg-[#e01f1f] disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 transition-colors inline-flex items-center gap-2">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Publishing…' : 'Publish listing'}
          </button>
        </div>
      </form>
    </div>
  );
}
